import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectorRef,
  AfterViewInit,
  AfterViewChecked,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { CommonModule } from "@angular/common";
import { AgGridAngular } from "ag-grid-angular";
import { ColDef, GridOptions, GridReadyEvent } from "ag-grid-community";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatDividerModule } from "@angular/material/divider";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { ReactiveFormsModule, FormsModule } from "@angular/forms";
import { Observable, Subscription, Subject, combineLatest } from "rxjs";
import { filter, take, takeUntil, map } from "rxjs/operators";
import { EditDeleteRendererComponent } from "@components/shared/edit-delete-renderer/edit-delete-renderer.component";
import { myCustomTheme } from "../../constants/grid-themes";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { AddTicketModalComponent } from "@components/modals/add-ticket-modal/add-ticket-modal.component";
import { TicketDetailsModalComponent } from "@components/modals/ticket-details-modal/ticket-details-modal.component";
import { Ticket, GetTicketsRequest } from "@models/ticket-data.model";
import { Store } from "@ngrx/store";
import {
  selectTicketLoading,
  selectTickets,
} from "@store/selectors/issue-tracker/issue-tracker.selectors";
import {
  selectOrganizationID,
  selectOrganizationName,
} from "@store/selectors/add-organization/add-organization.selectors";
import { IssueTrackerActions } from "@store/actions/issue-tracker/issue-tracker.actions";
import { selectOrgEMRId } from "@store/selectors/organization-emr-details/organization-emr-details.selector";
import { selectOrgs } from "@store/selectors/user-admin/user-admin.selector";
import { selectActiveUser } from "@store/selectors/user/user.selectors";
import { RoleConstants, ROLE_GROUPS } from "../../constants/roles";
import {
  selectImpactedMeasureOptions,
  ImpactedMeasureOption,
} from "@store/selectors/dashboard/dashboard.selectors";

ModuleRegistry.registerModules([AllCommunityModule]);

@Component({
  selector: "app-issue-tracker",
  standalone: true,
  imports: [
    CommonModule,
    AgGridAngular,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatDividerModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    FormsModule,
  ],
  templateUrl: "./issue-tracker.component.html",
  styleUrl: "./issue-tracker.component.css",
})
export class IssueTrackerComponent
  implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy
{
  private readonly dialog = inject(MatDialog);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly store = inject(Store);
  private readonly route = inject(ActivatedRoute);
  private readonly destroy$ = new Subject<void>();

  private pendingTicketId: number | null = null;
  private hasAutoOpened = false;

  theme = myCustomTheme;
  private gridApi: any;
  rowData$ = this.store.select(selectTickets);
  tickets: Ticket[] = [];
  subs: Subscription[] = [];

  organizations$ = this.store.select(selectOrgs);
  organizations: any[] = [];

  impactedMeasureOptions: ImpactedMeasureOption[] = [];

  activeUser$ = this.store.select(selectActiveUser);
  userRoles: string[] = [];
  userOrgIds: number[] = [];

  loading$ = this.store.select(selectTicketLoading);
  isLoading = true;

  showClosedTickets = false;

  orgId$ = this.store.select(selectOrganizationID);
  orgEMRId$ = this.store.select(selectOrgEMRId);

  orgDetails$ = combineLatest([this.orgId$, this.orgEMRId$]).pipe(
    map(([orgId, orgEMRId]) => ({
      organizationId: orgId ? parseInt(orgId) : null,
      organizationEMRId: orgEMRId ? parseInt(orgEMRId) : null,
    })),
    filter((details) => !!details.organizationId && !!details.organizationEMRId)
  );

  public gridOptions: GridOptions = {
    rowSelection: "single",
    pagination: true,
    paginationPageSize: 20,
    suppressClickEdit: true,
    editType: "fullRow",
    loading: true,
    rowData: [],
    onGridReady: this.onGridReady.bind(this),
  };

  colDefs: ColDef[] = [
    {
      field: "id",
      headerName: "Issue ID",
      width: 120,
      pinned: "left",
      editable: false,
      suppressNavigable: true,
    },
    {
      field: "subject",
      headerName: "Summary",
      flex: 2,
      tooltipValueGetter: (p: any) => p.value,
    },
    {
      field: "ticketCategoryName",
      headerName: "Category",
      width: 150,
      tooltipValueGetter: (p: any) => p.value,
    },
    {
      field: "measureCode",
      headerName: "Measure(s)",
      width: 140,
      valueGetter: (params: any) => this.getMeasureIdDisplay(params.data),
      tooltipValueGetter: (params: any) => this.getMeasureIdDisplay(params.data),
    },
    {
      field: "organizationId",
      headerName: "Organization",
      width: 180,
      valueGetter: (params: any) => {
        return this.getOrganizationName(params.data.organizationId);
      },
      tooltipValueGetter: (params: any) => {
        return this.getOrganizationName(params.data.organizationId);
      },
    },
    {
      field: "ticketStatusName",
      headerName: "Status",
      width: 120,
      cellRenderer: (params: any) => {
        if (!params.value) return "";
        const status = params.value;
        const statusClass = this.getStatusClass(status);
        return `<span class="status-badge ${statusClass}">${status}</span>`;
      },
    },
    {
      field: "submittedByName",
      headerName: "Submitted By",
      width: 180,
      tooltipValueGetter: (p: any) => p.value,
    },
    {
      field: "createDate",
      headerName: "Date Created",
      width: 140,
      cellDataType: "date",
      valueFormatter: (params: any) => {
        if (params.value) {
          return new Date(params.value).toLocaleDateString();
        }
        return "";
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      pinned: "right",
      filter: false,
      editable: false,
      suppressNavigable: true,
      cellRenderer: EditDeleteRendererComponent,
      cellRendererParams: {
        showDelete: false,
        showEdit: true,
        editButtonText: "View",
      },
    },
  ];

  defaultColDef: ColDef = {
    flex: 1,
    filter: true,
    resizable: true,
    sortable: true,
    editable: false,
    minWidth: 100,
  };

  ngOnInit(): void {
    this.isLoading = true;

    const ticketIdParam = this.route.snapshot.paramMap.get('id');
    if (ticketIdParam) {
      this.pendingTicketId = parseInt(ticketIdParam, 10);
    }

    this.organizations$.pipe(takeUntil(this.destroy$)).subscribe((orgs) => {
      this.organizations = orgs || [];
    });

    this.store
      .select(selectImpactedMeasureOptions)
      .pipe(takeUntil(this.destroy$))
      .subscribe((options) => {
        this.impactedMeasureOptions = options;
      });

    this.loading$.pipe(takeUntil(this.destroy$)).subscribe((loading) => {
      this.isLoading = loading;
      if (this.gridApi) {
        this.gridApi.setGridOption("loading", loading);
      }
    });

    this.activeUser$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      if (user) {
        this.userRoles = user.roles || [];
        this.userOrgIds = user.loggedInUser?.organizationIds || [];
      }
    });

    this.rowData$.pipe(takeUntil(this.destroy$)).subscribe((tickets) => {
      if (tickets && tickets.length > 0) {
        this.tickets = tickets;
        this.applyFiltering();
        this.tryAutoOpenTicket();
      } else if (!this.isLoading) {
        this.tickets = tickets || [];
        this.applyFiltering();
      }
    });

    this.loadTickets();
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  ngAfterViewChecked(): void {
    this.cdr.detectChanges();
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.gridApi.setGridOption("loading", this.isLoading);

    if (this.tickets.length > 0) {
      this.applyFiltering();
    }
  }

  onCellClicked(params: any): void {
    if (
      params.column.colId === "actions" &&
      params.event.target.dataset.action
    ) {
      let action = params.event.target.dataset.action;
      if (action === "edit") {
        this.viewTicketDetails(params.data.id);
      }
    }
  }

  viewTicketDetails(ticketId: number): void {
    const selectedTicket = this.tickets.find(
      (ticket) => ticket.id === ticketId
    );

    if (!selectedTicket) {
      console.error("Ticket not found in local data:", ticketId);
      return;
    }

    const dialogRef = this.dialog.open(TicketDetailsModalComponent, {
      width: "80vw",
      maxWidth: "1200px",
      disableClose: false,
      data: {
        ticketId,
        ticketData: selectedTicket,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.updated) {
        this.loadTickets();
      }
    });
  }

  private tryAutoOpenTicket(): void {
    if (this.pendingTicketId && !this.hasAutoOpened) {
      const ticket = this.tickets.find((t) => t.id === this.pendingTicketId);
      if (ticket) {
        this.hasAutoOpened = true;
        this.pendingTicketId = null;
        this.viewTicketDetails(ticket.id);
      }
    }
  }

  private loadTickets(): void {
    this.orgDetails$
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((orgDetails) => {
        const request: GetTicketsRequest = {
          organizationId: orgDetails.organizationId!,
          organizationEMRId: orgDetails.organizationEMRId!,
        };
        this.store.dispatch(IssueTrackerActions.getTickets({ request }));
      });
  }

  private getStatusClass(status: string): string {
    switch (status) {
      case "New":
        return "status-new";
      case "In Progress":
        return "status-in-progress";
      case "Closed":
        return "status-closed";
      default:
        return "";
    }
  }

  private getMeasureIdDisplay(ticket: Ticket): string {
    if (ticket.missingMeasureCodes?.length) {
      return ticket.missingMeasureCodes
        .map((code) => this.getMeasureTitle(code))
        .join(", ");
    }
    return ticket.measureCode || "";
  }

  private getMeasureTitle(measureCode: string): string {
    const option = this.impactedMeasureOptions.find((candidate) => candidate.code === measureCode);
    return option?.title || measureCode;
  }

  getOrganizationName(organizationId: number): string {
    if (!this.organizations || this.organizations.length === 0) {
      return `Org ${organizationId}`;
    }

    const org = this.organizations.find(
      (o) => Number(o.id) === Number(organizationId)
    );
    return org?.name || `Org ${organizationId}`;
  }

  private isSuperUser(): boolean {
    return ROLE_GROUPS.SUPER_USER.some(role => this.userRoles.includes(role));
  }

  private applyFiltering(): void {
    let filteredData = this.tickets;

    if (!this.isSuperUser()) {
      filteredData = filteredData.filter((ticket) =>
        this.userOrgIds.includes(ticket.organizationId)
      );
    }
    if (!this.showClosedTickets) {
      filteredData = filteredData.filter(
        (ticket) => ticket.ticketStatusName?.toLowerCase() !== "closed"
      );
    }
    if (this.gridApi) {
      if (!this.isLoading) {
        this.gridApi.setGridOption("rowData", filteredData);
      }
    }
  }

  onShowClosedChange(): void {
    this.applyFiltering();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.subs.forEach((sub) => {
      sub?.unsubscribe();
    });
  }
}
